/**
 * =============================================================================
 * PATIENT JOURNEY - RENDERING HELPERS
 * =============================================================================
 *
 * The workflow graph itself - the stages, the steps, the options, the
 * transitions, the validation rules and the terminal states - lives on the
 * backend and arrives with each case in the journey payload. Adding a step is a
 * change to a definition file on the server, not to this app.
 *
 * What is left here is the small amount of formatting the components need to
 * turn that payload into cards, and it decides nothing about the workflow.
 * =============================================================================
 */

/* -----------------------------------------------------------------------------
   NODE TYPES

   The vocabulary the server uses on `node.type`. A renderer switches on these.
----------------------------------------------------------------------------- */

export const NODE_TYPE = {
    ACKNOWLEDGMENT: "acknowledgment",
    OUTCOME: "outcome",
    FORM: "form",
};

/* -----------------------------------------------------------------------------
   OPTION TONES - drives the colour of a selected outcome card
----------------------------------------------------------------------------- */

export const OPTION_TONE = {
    DEFAULT: "default",
    WARNING: "warning",
    DANGER: "danger",
};

/* -----------------------------------------------------------------------------
   FALLBACK COPY

   Used only when a definition does not name its own label.
----------------------------------------------------------------------------- */

export const TIMESTAMP_LABEL = "Date & Time (optional)";

/* -----------------------------------------------------------------------------
   HELPERS
----------------------------------------------------------------------------- */

/**
 * "STEP 2" / "STEP 5.1" - the caption above a step card. The server sends
 * `stepLabel` already resolved; this is the fallback for a node that does not
 * carry one.
 */
export const getStepLabel = (node, stage) => {
    if (!node) {
        return "";
    }

    if (node.stepLabel) {
        return node.stepLabel;
    }

    const resolvedStage =
        typeof node.stage === "number" ? node.stage : stage;

    return typeof resolvedStage === "number"
        ? "STEP " + resolvedStage
        : "STEP";
};

/**
 * Sub questions captured under a selected option row.
 */
export const getOptionFields = (option) => option?.fields || [];

/**
 * Drives the "End Case" chip on an option row. Resolved by the backend, which
 * knows whether the option closes the case outright or by opening a sub flow
 * that can only close it.
 */
export const optionEndsCase = (option) => Boolean(option?.endsCase);

/**
 * Whether the timestamp field blocks saving. Optional unless the step opts in.
 */
export const isTimestampRequired = (node, option) =>
    Boolean(option?.requiresTimestamp) && node?.timestampRequired === true;

/**
 * Pre-selected answers for a set of fields, so the segmented toggles open on
 * their default choice instead of blank.
 */
export const getDefaultFieldValues = (fields) =>
    (fields || []).reduce((collected, field) => {
        if (field.defaultValue !== undefined && field.defaultValue !== null) {
            collected[field.name] = field.defaultValue;
        }

        return collected;
    }, {});

/**
 * The labelled pairs shown on a locked summary card - "Exit Type / LAMA" rather
 * than "exitType / lama". The backend resolves these against the definition the
 * step was recorded under, so a later definition can never relabel history.
 */
export const getEntryFieldSummary = (entry) => entry?.details || [];
